import React from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Banner } from '@wartek-id/banner'

import { useKinerjaHarianActive } from 'app/KinerjaHarian/hooks/useKinerjaHarianActive'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'

interface CardKinerjaHarianProps {
  ctaHref?: string
  ctaText?: string
  disabled?: boolean
  isLoading?: boolean
  onClick?: () => void | Promise<void>
}

export default function CardKinerjaHarian({
  ctaHref,
  ctaText = 'Ke Kinerja Harian',
  disabled = false,
  isLoading = false,
  onClick,
}: CardKinerjaHarianProps) {
  const router = useRouter()
  const {
    isActive,
    isPerencanaanReady,
    isLoading: isLoadingKinerjaHarianActive,
  } = useKinerjaHarianActive()
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const handleCta = async () => {
    await onClick?.()

    trackEvent({
      activityType: 'click_kinerja_harian',
      screenName: 'beranda',
    })

    if (ctaHref) {
      router.push(ctaHref)
    }
  }

  React.useEffect(() => {
    if (isActive) {
      trackEvent({
        activityType: 'page_view',
        screenName: 'beranda',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  if (isLoadingKinerjaHarianActive || !isActive) {
    return null
  }

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4 lg:flex-row lg:gap-6 lg:p-6">
        <div className="flex flex-1 gap-4 lg:gap-6">
          <div className="relative h-[72px] w-[72px] shrink-0 lg:h-[84px] lg:w-[84px]">
            {/* Prevent user select */}
            <div className="absolute inset-0 z-10">&nbsp;</div>

            <Image
              alt="Kinerja Harian"
              src="/illustrations/img-book-journal.png"
              width={84}
              height={84}
            />
          </div>

          <div className="flex flex-col justify-center flex-1 gap-1">
            <Text variant="heading-lg" as="h2">
              Kinerja Harian
            </Text>
            <Text variant="body" color="subdued">
              Catat kinerja harian agar selaras dan turut mendukung kinerja
              periodik dan tahunan, sesuai Permen PANRB No. 6 Tahun 2022 dan
              kebijakan <strong>Pemerintah Daerah</strong>.
            </Text>
          </div>
        </div>

        <div className="flex pl-[88px] lg:block lg:w-[240px] lg:min-w-[240px] lg:pl-0">
          <Button
            size="lg"
            color="black"
            disabled={disabled || !isPerencanaanReady}
            isLoading={isLoading}
            onClick={handleCta}
            data-testid="cta-button"
            className="px-8 lg:w-full"
          >
            {ctaText}
          </Button>
        </div>
      </div>

      {!isPerencanaanReady && (
        <div className="px-4 pb-6 lg:px-6">
          <Banner
            type="warning"
            title="Kinerja Harian baru dapat dicatat setelah Perencanaan Kinerja Anda sudah selesai"
            className="!pb-2"
            closable={false}
          />
        </div>
      )}
    </Card>
  )
}
